import { Button } from "@zapier/design-system-beta";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { Suspense } from "react";
import { Edge, Node } from "@xyflow/react";

import getBaseUrl from "../../utils/getBaseUrl";

import styles from "./GraphData.module.css";
import {
  autoLayoutSetter,
  edgesAtom,
  nodesAtom,
  uuid,
} from "@zapier/canvas-state";

export function GraphData() {
  return (
    <Suspense>
      <GraphDataInner />
    </Suspense>
  );
}

const buildCanvasFromAssetsSetter = atom(
  null,
  (_get, set, dbNodesStr: string, dbEdgesStr: string) => {
    const dbNodes = JSON.parse(dbNodesStr);
    const dbEdges = JSON.parse(dbEdgesStr);

    // Create a map to store the generated UUIDs for each asset_id and zapId for steps
    const idMap: { [key: string]: string } = {};

    // First pass: Create UUIDs for zaps and populate idMap
    dbNodes.forEach((node: any) => {
      if (node.asset_type === "zap") {
        const generatedId = uuid(); // Generate a new UUID for zap nodes
        idMap[node.asset_id] = generatedId;
      }
    });

    // Helper to find the zapId for a given step id
    const findZapIdForStep = (stepId: string): string | undefined => {
      const matchingEdge = dbEdges.find(
        (edge: any) =>
          edge.src_asset_id === stepId &&
          edge.src_type === "step" &&
          edge.relationship === "IS_PART_OF",
      );
      return matchingEdge ? matchingEdge.dst_asset_id : undefined;
    };

    // Second pass: Assign UUIDs for steps based on the zap they are part of
    dbNodes.forEach((node: any) => {
      if (node.asset_type === "step") {
        const zapId = findZapIdForStep(node.asset_id);
        if (zapId && idMap[zapId]) {
          // Use the UUID of the zap this step is part of
          idMap[node.asset_id] = idMap[zapId];
        }
      } else if (!idMap[node.asset_id]) {
        // For non-step, non-zap nodes, assign a new UUID
        idMap[node.asset_id] = uuid();
      }
    });

    // Convert dbNodes to nodes, return null for step nodes (they will be filtered later)
    const nodes: Node[] = dbNodes
      .map((node: any) => {
        // Handle the node data for non-step nodes
        if (node.asset_type === "step") {
          return null; // Return null for step nodes to filter them later
        }

        const nodeData: any = {
          label: node.asset_type,
        };

        if (node.asset_type === "table") {
          nodeData.tableId = node.asset_id;
        } else if (node.asset_type === "zap") {
          nodeData.zapId = node.asset_id;
          nodeData.isExpanded = true;
        } else if (node.asset_type === "interface") {
          nodeData.projectId = node.asset_id;
        }

        return {
          id: idMap[node.asset_id],
          type: node.asset_type === "zap" ? "zap" : "default", // Assuming default type for table and zap for interface
          data: nodeData,
          position: { x: Math.random() * 800, y: Math.random() * 600 }, // Random position; adjust as needed
        };
      })
      .filter((node: Node) => node !== null); // Filter out null nodes (i.e., step nodes)

    // Convert dbEdges to edges using the idMap to set source and target
    const edges: Edge[] = dbEdges
      .filter(
        (edge: any) =>
          !(edge.src_type === "step" && edge.dst_type === "step") && // Exclude edges between two steps
          edge.relationship !== "IS_PART_OF", // Exclude edges with "IS_PART_OF" relationship
      )
      .map((edge: any) => {
        let sourceId = idMap[edge.src_asset_id];
        let targetId = idMap[edge.dst_asset_id];

        // If the source or target is a step, construct the id in the format "zap-${zapStepNodeId}-${zapId}"
        if (edge.src_type === "step") {
          const zapId = findZapIdForStep(edge.src_asset_id);
          if (zapId) {
            sourceId = `zap-${idMap[edge.src_asset_id]}-${edge.src_asset_id}`;
          }
        }
        if (edge.dst_type === "step") {
          const zapId = findZapIdForStep(edge.dst_asset_id);
          if (zapId) {
            targetId = `zap-${idMap[edge.dst_asset_id]}-${edge.dst_asset_id}`;
          }
        }

        return {
          id: uuid(),
          source: sourceId,
          target: targetId,
          sourceHandle: "right",
          targetHandle: "left",
          label: edge.relationship,
          data: {
            createdBy: "databricks",
          },
        };
      });

    // Set the resulting nodes and edges
    set(nodesAtom, nodes);
    set(edgesAtom, edges);
    set(autoLayoutSetter);
  },
);

function GraphDataInner() {
  const assetData = useAtomValue(assetDataAtom);
  const handleDatabricksQuery = useSetAtom(handleDatabricksQueryAtom);
  const buildCanvasFromAssets = useSetAtom(buildCanvasFromAssetsSetter);

  return (
    <div>
      <Button onPress={() => handleDatabricksQuery()}>
        Get Complex Systems
      </Button>
      <ul className={styles.systemsList}>
        {assetData.map(([_accountId, dbNodes, dbEdges], index) => (
          <li key={index} className={styles.systemWrapper}>
            System {index}:{" "}
            <Button onPress={() => buildCanvasFromAssets(dbNodes, dbEdges)}>
              Canvas
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

type AssetMetadataResponse = Array<[string, string, string]>;

const assetDataAtom = atom<AssetMetadataResponse>([]);

const fetchAssetDataAtom = atom(null, async (get, set) => {
  const statementId = get(statementIdAtom);
  const res = await fetch(
    `${getBaseUrl()}/api/canvasui/databricks?statementId=${statementId}`,
  );
  const json = await res.json();
  set(assetDataAtom, json.result.data_array);
});

const runStatementAtom = atom(null, async (get, set) => {
  const statement = get(statementAtom);
  const res = await fetch(`${getBaseUrl()}/api/canvasui/databricks`, {
    method: "POST",
    body: JSON.stringify({ statement }),
  });
  const json = await res.json();
  set(statementIdAtom, json.statement_id);
});

const handleDatabricksQueryAtom = atom(null, async (get, set) => {
  await set(runStatementAtom);
  await set(fetchAssetDataAtom);
});

const statementIdAtom = atom("");
const statementAtom = atom(
  "select * from production_modeled.public.connected_systems_connected_assets WHERE account_id=2074697 LIMIT 30;",
);
